import { Component, Inject, OnInit } from '@angular/core';
import { RmsModule } from '@rms';
import { RmsPulldownRef } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { OrdersDataService } from '../../../../../../services/orders-data.service';
import { PAYMENT_STATUSES } from '@shared/constants/payment-status';
import { ACH_PULL_PAYMENT_METHOD_ID } from '@shared/constants/payment-method';


function computeMaskedReference(rawInput: string | null | undefined): string | null {
  const digits = String(rawInput || '').replace(/\D/g, '');
  if (!digits) return null;
  const hiddenCount = Math.max(0, digits.length - 4);
  return '*'.repeat(hiddenCount) + digits.slice(-4);
}

@Component({
  selector: 'app-payment-form',
  imports: [RmsModule],
  templateUrl: './payment-form.component.html',
  styleUrl: './payment-form.component.scss'
})
export class PaymentFormComponent implements OnInit {
  Payment: any = {};
  OrderOptions: any[] = [];
  PaymentMethods: any[] = [];
  Statuses = PAYMENT_STATUSES;
  IsEdit = false;
  OrderBalances: Record<number, { OrderTotal: number; OrderPaid: number; OrderBalanceDue: number }> = {};
  PendingPaymentsByOrder: Record<number, { OrderPaymentID: number; MethodName: string; StatusName: string; Amount: number }[]> = {};


  CardNumberInput = '';

  constructor(
    @Inject(RMS_PULLDOWN_DATA) private data: any,
    public ref: RmsPulldownRef,
    private ordersData: OrdersDataService
  ) {
    this.Payment = { ...data };
    delete this.Payment.Orders;
    delete this.Payment.OrderBalances;
    delete this.Payment.PendingPaymentsByOrder;
    this.IsEdit = this.Payment.OrderPaymentID != null;
    this.OrderOptions = (data.Orders || []).map((o: any) => ({ OrderID: o.OrderID, OrderLabel: `Order #${o.OrderID}` }));
    this.OrderBalances = data.OrderBalances || {};
    this.PendingPaymentsByOrder = data.PendingPaymentsByOrder || {};
  }

  // Warns before a double-charge happens, rather than after - e.g. someone about to add
  // a second payment on an order that already has an ACH Pull sitting "Requested". Excludes
  // this payment's own row when editing, so an ACH payment doesn't warn against itself.
  get pendingPaymentsForSelectedOrder(): { OrderPaymentID: number; MethodName: string; StatusName: string; Amount: number }[] {
    const pending = this.PendingPaymentsByOrder[this.Payment.OrderID] || [];
    if (!this.IsEdit) return pending;
    return pending.filter(p => p.OrderPaymentID !== this.Payment.OrderPaymentID);
  }

  get maskedPreview(): string | null {
    return computeMaskedReference(this.CardNumberInput);
  }

  get selectedOrderBalance() {
    return this.Payment.OrderID != null ? this.OrderBalances[this.Payment.OrderID] : null;
  }

  get isAchPull(): boolean {
    return this.Payment.PaymentMethodID === ACH_PULL_PAYMENT_METHOD_ID;
  }

  ngOnInit() {
    this.ordersData.getRefData({ keys: ['PaymentMethods'] }).then(({ PaymentMethods }) => {
      this.PaymentMethods = PaymentMethods || [];
    });
  }

  get isValid(): boolean {
    const achEmailOk = !this.isAchPull || !!String(this.Payment.AuthorizationRecipientEmail || '').trim();
    return !!this.Payment.OrderID && !!this.Payment.PaymentMethodID && !!this.Payment.StatusID
      && Number(this.Payment.Amount) > 0 && achEmailOk;
  }

  private buildSavePayload(overrides: any = {}): any {
    const maskedReference = this.CardNumberInput ? this.maskedPreview : this.Payment.MaskedReference;
    return { ...this.Payment, MaskedReference: maskedReference, ...overrides, cancelled: false };
  }

  recalculateFee() {
    const amount = Number(this.Payment.Amount) || 0;
    const feePercent = Number(this.Payment.FeePercent) || 0;
    this.Payment.FeeAmount = Math.round(amount * feePercent) / 100;
  }

  save() {
    if (!this.isValid) return;
    this.ref.close(this.buildSavePayload());
  }

  saveAndSendAuthorization() {
    if (!this.isValid) return;
    this.ref.close(this.buildSavePayload({
      StatusID: 1,
      ActionDescription: 'ACH authorization request sent'
    }));
  }

  cancel() {
    this.ref.close({ cancelled: true });
  }
}
